import clsx from 'clsx';
import { isArray, pull } from 'lodash';
import { bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { FieldErrorMessage, Icon } from 'components';

import 'assets/styles/components/form/form-checkbox.scss';

const FormCheckbox = ({
  name,
  labelI18nKey = '',
  labelI18nValues = {},
  customLabel = null,
  customIcon = null,
  value = null,
  inputRef = null,
  rules = {},
  className = '',
  labelClassName = '',
  required = false,
  disabled = false,
  readOnly = false,
  link = null,
  onChange = null
}) => {
  const { t } = useTranslation();
  const {
    getValues,
    setValue,
    register,
    formState: { errors },
    control
  } = useFormContext();
  const checkedValues = useWatch({ control, name });

  const isChecked = useMemo(() => {
    /*
      Check if value is in array of values (for multiple value checkboxes)
      or if value is equal to value (for single value checkbox)
    */
    return isArray(checkedValues) ? checkedValues.includes(value) : checkedValues || false;
  }, [checkedValues, value]);

  const handleOnChange = (e) => {
    if (onChange) {
      // onChange event can be handled from parent component using this prop
      onChange(e.target.checked, e.target.value);
    } else {
      // If no onChange event is provided, update the value in the form context
      let newValue = e.target.checked;
      if (isArray(getValues(name))) {
        newValue = e.target.checked ? [...getValues(name), e.target.value] : pull(getValues(name), e.target.value);
      }

      setValue(name, newValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
  };

  return (
    <div
      className={clsx('checkbox-container', className, {
        checked: isChecked,
        disabled: disabled || readOnly
      })}
      aria-live="polite"
    >
      <label className={clsx('checkbox-label', labelClassName)}>
        <input
          ref={inputRef}
          name={name}
          type="checkbox"
          className="input sr-only"
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          {...(value && { value })}
          {...(register && register(name, { ...rules, onChange: (e) => handleOnChange(e) }))}
        />

        <div className="box">{customIcon ? customIcon : isChecked && <Icon name="check" classes="check-icon" />}</div>

        {link && (
          <Trans i18nKey={labelI18nKey} values={link.i18nValues || {}}>
            <a
              href={link.url}
              target={link.target}
              onClick={link.onClick}
              rel="noopener noreferrer"
              className={clsx('text-primary-500', link.className)}
            >
              {t(link.labelI18nKey)}
            </a>
          </Trans>
        )}

        {customLabel && customLabel()}

        {!link && !customLabel && <span>{t(labelI18nKey, { ...labelI18nValues })}</span>}
      </label>

      <FieldErrorMessage errors={errors} name={name} />
    </div>
  );
};

FormCheckbox.propTypes = {
  name: string.isRequired,
  labelI18nKey: string,
  labelI18nValues: object,
  customLabel: func,
  customIcon: node,
  value: oneOfType([string, bool]),
  inputRef: oneOfType([func, object]),
  rules: object,
  className: string,
  labelClassName: string,
  required: bool,
  disabled: bool,
  readOnly: bool,
  link: shape({ url: string, labelKey: string, target: string, className: string, i18nValues: object }),
  onChange: func
};

export default FormCheckbox;
